// @flow

import React, { Component } from "react";
import type {
  ImportanceType,
  RaceStatusEnum,
  RaceVideoFeedback,
  RaceListItem
} from "@tvg/types/Race";
import type { NullaryFn } from "@tvg/types/Functional";
import { noop, get } from "lodash";

import RaceInfoInline from "../../_molecule/RaceInfoInline";
import RaceNavigation from "../../_organism/RaceNavigation";
import RaceHeaderContainer, {
  PoolsInfoButtonWrapper,
  VideoButtonWrapper,
  PoolsInfoButton,
  WrapSpan,
  ControlButtonsContainer,
  RaceNavigationContainer,
  RaceInfoContainer,
  RaceArrowNavigation,
  RaceArrowLink,
  RaceArrowButton
} from "./styled-components";
import VideoButton from "../../_molecule/VideoButton";
import {
  infoOutline,
  arrowBack,
  arrowForward
} from "../../_static/Icons/icons";
import Icon from "../../_static/Icons";

import buildColor from "../../_static/ColorPalette";

type RaceInfoType = {
  mtp: number,
  raceNumber: string,
  trackAbbr: string,
  trackName: string,
  status: RaceStatusEnum,
  postTime: string,
  isGreyhound: boolean
};

type RaceDataType = {
  highlightedDescription: string,
  importanceLevel: ImportanceType,
  raceInfo: RaceListItem[]
};

type Props = {
  /**
   * Basic info for the track
   */
  raceInfo: RaceInfoType,
  /**
   * Inline data of this race
   */
  raceData: RaceDataType,
  /**
   * open race navigation modal
   */
  openRaceNavigation: NullaryFn<void>,
  /**
   * open pools & info modal
   */
  openPoolsNInfo: NullaryFn<void>,
  /**
   * Video Button state
   */
  videoButton: {
    feedback: RaceVideoFeedback,
    small: boolean,
    onClick: NullaryFn<mixed>
  },

  /**
   * Variable to show or hide controll buttons, applying transition
   */
  dismissControllButtons: boolean,
  /**
   * Variable to render or not the race navigation
   */
  isControlsDisabled: boolean,
  /**
   * Variable to show or hide race navigation
   */
  dismissRaceNavigation: boolean,
  /**
   * Enables hability to stick to the top of the parent container (for tablet layout)
   */
  isSticky: boolean,
  /**
   * The next race's url within this track
   */
  nextRaceUrl: string,
  /**
   * The previous race's url within this track
   */
  previousRaceUrl: string,
  /**
   * Whenever the track has a race next (false if it the last race)
   */
  hasNextRace: boolean,
  /**
   * Whenever the track has a race before (false if it the first race)
   */
  hasPreviousRace: boolean,
  /*
   * Callback for arrow navigation clicking
   */
  onRaceArrowNavigationClick: NullaryFn<mixed>,
  /**
   * Toggle value that enables/disenables the promo badge
   */
  hasPromoBadge: boolean,
  /**
   * callback for gtm usage
   */
  onPromoClick: NullaryFn<void>,

  enablePromosStoryblok: boolean,

  oldPromosURLs: string[],
  /**
   * is promo external
   */
  isExternal: boolean,
  useIsPromoTagShownFlag: boolean
};

class RaceCardHeader extends Component<Props> {
  static defaultProps = {
    openRaceNavigation: noop,
    openPoolsNInfo: noop,
    dismissControllButtons: false,
    dismissRaceNavigation: false,
    isControlsDisabled: false,
    isSticky: false,
    nextRaceUrl: "",
    previousRaceUrl: "",
    hasNextRace: true,
    hasPreviousRace: true,
    onRaceArrowNavigationClick: noop,
    hasPromoBadge: true,
    onPromoClick: noop,
    enablePromosStoryblok: false,
    oldPromosURLs: [],
    isExternal: false,
    useIsPromoTagShownFlag: false
  };

  renderRaceInfo = () => {
    const raceInfo = get(this, "props.raceInfo");

    const promo = this.props.hasPromoBadge ? raceInfo.promo : null;

    return (
      <RaceInfoContainer>
        <RaceInfoInline
          description={get(this.props, "raceData.highlightedDescription")}
          importanceLevel={get(this.props, "raceData.importanceLevel")}
          raceData={get(this.props, "raceData.raceInfo")}
          isExternalPromo={get(this.props, "isExternal")}
          raceNumber={get(this.props, "raceInfo.raceNumber")}
          promo={promo}
          enablePromosStoryblok={this.props.enablePromosStoryblok}
          onPromoClick={
            this.props.hasPromoBadge ? this.props.onPromoClick : noop
          }
          isDark
          useAltLink
          isRaceDataChangesShown
          useIsPromoTagShownFlag={this.props.useIsPromoTagShownFlag}
        />
      </RaceInfoContainer>
    );
  };

  renderNavigation = (
    showControls: boolean = false,
    showArrowNavigation: boolean = false
  ) => {
    const NextRaceButton = this.props.hasNextRace
      ? RaceArrowLink
      : RaceArrowButton;
    const PreviousRaceButton = this.props.hasPreviousRace
      ? RaceArrowLink
      : RaceArrowButton;

    return (
      <RaceNavigationContainer
        dismissRaceNavigation={this.props.dismissRaceNavigation}
        onVideo={get(this.props, "video.show")}
      >
        <RaceNavigation
          {...this.props.raceInfo}
          onClickCallback={this.props.openRaceNavigation}
          isSmall={get(this.props, "video.show") && showControls}
          isMicro={get(this.props, "video.show") && !showControls}
        />
        {showArrowNavigation && (
          <RaceArrowNavigation>
            <PreviousRaceButton
              to={this.props.previousRaceUrl}
              onClick={this.props.onRaceArrowNavigationClick}
            >
              <Icon
                icon={arrowBack}
                size={16}
                color={
                  this.props.hasPreviousRace
                    ? buildColor("white", "100")
                    : buildColor("blue", "700")
                }
              />
            </PreviousRaceButton>
            <NextRaceButton
              to={this.props.nextRaceUrl}
              onClick={this.props.onRaceArrowNavigationClick}
            >
              <Icon
                icon={arrowForward}
                size={16}
                color={
                  this.props.hasNextRace
                    ? buildColor("white", "100")
                    : buildColor("blue", "700")
                }
              />
            </NextRaceButton>
          </RaceArrowNavigation>
        )}
      </RaceNavigationContainer>
    );
  };

  render() {
    return (
      <RaceHeaderContainer
        data-qa-label="raceCardHeader"
        id="raceCardHeader"
        isSticky={this.props.isSticky}
      >
        {this.renderNavigation(false, true)}
        {this.renderRaceInfo()}

        {!this.props.isControlsDisabled && (
          <ControlButtonsContainer
            dismissControllButtons={this.props.dismissControllButtons}
          >
            <PoolsInfoButtonWrapper>
              <PoolsInfoButton
                qaLabel="raceCardHeader-PoolsInfoBtn"
                type="secondary_alt"
                isStretched
                isBold={false}
                onClick={this.props.openPoolsNInfo}
              >
                <Icon
                  icon={infoOutline}
                  size={16}
                  color={buildColor("white", "100")}
                />
                <WrapSpan>Pools & Info</WrapSpan>
              </PoolsInfoButton>
            </PoolsInfoButtonWrapper>
            <VideoButtonWrapper>
              <VideoButton {...this.props.videoButton} />
            </VideoButtonWrapper>
          </ControlButtonsContainer>
        )}
      </RaceHeaderContainer>
    );
  }
}

export default RaceCardHeader;
